import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { TargetingLocation } from 'types/geolocationTypes';
import { SaveRefused } from 'hooks/useAutoSave';

/**
 * What the targeting screen holds, as one plain value: what auto-save compares
 * and what one undo step puts back.
 */
export interface TargetingWork {
  vac_name: string;
  brand: number | null;
  job_type: number | null;
  sub_job_type: number | null;
  language: number | null;
  experience: string[];
  location: TargetingLocation[];
}

/** The function type that needs no subtype ("other"). */
const JOB_TYPE_WITHOUT_SUBTYPE = 18;

/**
 * What is still missing before the screen CAN be stored, in the words the
 * screen already uses for it. The server refuses an incomplete targeting
 * screen whole, and it deletes every stored location that a request leaves
 * out, so an incomplete screen is not sent at all.
 */
export const missingForTargeting = (work: TargetingWork): string[] => [
  ...(!work.vac_name.trim() ? ['No function title provided'] : []),
  ...(!work.brand ? ['No name provided'] : []),
  ...(!work.job_type ? ['No function type provided'] : []),
  ...(!(work.sub_job_type || work.job_type === JOB_TYPE_WITHOUT_SUBTYPE)
    ? ['No function subtype provided']
    : []),
  ...(!work.language ? ['No language provided'] : []),
  ...(!work.location.length ? ['No locations provided'] : []),
];

/** The corner's words while the screen cannot be stored. Not yet approved. */
export const notSavedBecause = (missing: string[]): string | undefined =>
  missing.length
    ? `Not saved: ${missing[0].charAt(0).toLowerCase()}${missing[0].slice(1)}`
    : undefined;

const samePlace = (a: TargetingLocation, b: TargetingLocation): boolean => {
  const lngOf = (l: TargetingLocation) =>
    Number(l.lng ?? (l as unknown as { lon?: number }).lon);
  return (
    Number(a.lat) === Number(b.lat) &&
    lngOf(a) === lngOf(b) &&
    (a.name ?? '') === (b.name ?? '')
  );
};

/**
 * The body the targeting endpoint expects, the same the Save button sent.
 *
 * The number of each location is taken from what the SERVER holds now, never
 * from the screen: the server treats a location with a number as "only the
 * radius may differ" and fails on a number it no longer has, which is exactly
 * what an Undo of a removed location would send. A location the server does
 * not hold goes without a number and is made anew.
 */
export const targetingPayload = (
  work: TargetingWork,
  stored: TargetingLocation[]
): Record<string, unknown> => {
  const free = [...stored];
  const locations = work.location.map((location) => {
    const at = free.findIndex((held) => samePlace(held, location));
    const held = at >= 0 ? free.splice(at, 1)[0] : undefined;
    const rest: Partial<TargetingLocation> = { ...location };
    delete rest.id;
    return {
      ...rest,
      ...(held?.id ? { id: held.id } : {}),
      lon: location.lng,
    };
  });

  return {
    locations,
    can_exp: work.experience.length ? work.experience : ['exp_none'],
    can_range: 16,
    job_type: work.job_type ?? 0,
    sub_job_type: work.sub_job_type ?? null,
    language_id: work.language ?? 141,
    vac_name: work.vac_name,
    brand_id: work.brand ?? null,
  };
};

export const targetingPath = (vacancyId: number): string =>
  `/v4/vacancies/${vacancyId}/targeting`;

const headers = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
  'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
});

const signedOut = (): never => {
  if (window.location.pathname !== '/') {
    leaveSignedOut();
  }
  throw new Error('The session has ended.');
};

/** The locations the server holds for this campaign right now. */
export const storedLocations = async (
  vacancyId: number,
  signal?: AbortSignal
): Promise<TargetingLocation[]> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(
    `${base}/v4/vacancies/${vacancyId}?include=targeting`,
    { headers: headers(), signal }
  );
  if (response.status === 401) signedOut();
  if (!response.ok) {
    throw new Error(`Targeting was not read (${response.status}).`);
  }
  const body = (await response.json()) as {
    data?: { targeting?: unknown };
  };
  // The server wraps a thing, and a list inside it, in "data" each.
  const unwrap = (thing: unknown): unknown =>
    (thing as { data?: unknown } | undefined)?.data ?? thing;
  const targeting = unwrap(body.data?.targeting) as
    | { locations?: unknown }
    | undefined;
  const locations = unwrap(targeting?.locations);
  return Array.isArray(locations) ? (locations as TargetingLocation[]) : [];
};

/** The server refused the screen's own store: the campaign is published. */
export class TargetingIsPublished extends Error {
  constructor() {
    super('The campaign is published.');
    this.name = 'TargetingIsPublished';
  }
}

/**
 * Stores the targeting screen, quietly (no toast: the corner already says
 * what is happening, and auto-save retries by itself).
 *
 * @throws when the server did not confirm the work, or refused it
 */
export const saveTargeting = async (
  vacancyId: number,
  payload: Record<string, unknown>,
  signal?: AbortSignal
): Promise<number | null> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${targetingPath(vacancyId)}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(payload),
    signal,
  });
  if (response.status === 401) signedOut();
  if (response.status === 409) {
    const refused = (await response.json().catch(() => ({}))) as {
      out_of_date?: boolean;
      data?: {
        by?: string | null;
        own?: boolean;
        at?: string | null;
        kept_version?: number;
      };
    };
    // D10: the screen is out of date. Not a failure: the person chooses.
    if (refused.out_of_date) {
      throw new SaveRefused({
        by: refused.data?.by ?? null,
        own: refused.data?.own === true,
        at: refused.data?.at ?? null,
        keptVersion: Number(refused.data?.kept_version ?? 0),
      });
    }
    throw new TargetingIsPublished();
  }
  if (!response.ok) {
    throw new Error(`Targeting was not stored (${response.status}).`);
  }
  const body = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { kept_version?: number };
  };
  if (body.success === false) throw new Error('Targeting was not stored.');
  // The count the server holds now, for the next save of this screen.
  return typeof body.data?.kept_version === 'number'
    ? body.data.kept_version
    : null;
};
